import { domainImageUrl, toMediaSrc } from './imageProxy';
import { decodeMultipartFilename } from './multipartFilename';
import { parseVideoLink, VIDEO_PROVIDER_LABELS } from './videoSource';
import { getArticlePhotos } from './articleItemMediaUtils';

export const GALLERY_KIND_LABEL = {
  photo: 'Photo',
  document: 'Document',
  video: 'Vidéo',
};

const FILE_NAME_EXT = /\.(jpe?g|png|webp|gif|pdf|mp4|mov|avi|webm)$/i;

export function defaultGalleryLabel(kind, existingCount) {
  const base = GALLERY_KIND_LABEL[kind] || 'Média';
  if (!existingCount) {
    return base;
  }
  return `${base} ${existingCount + 1}`;
}

export function isFilenameLabel(title, filename) {
  const value = String(title || '').trim();
  if (!value) {
    return true;
  }
  const file = String(filename || '').trim();
  if (file && value.toLowerCase() === file.toLowerCase()) {
    return true;
  }
  return FILE_NAME_EXT.test(value);
}

export function formatMediaSize(bytes) {
  const size = Number(bytes);
  if (!size || size <= 0) {
    return '';
  }
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} Ko`;
  }
  const mo = size / (1024 * 1024);
  const text = mo >= 10 ? String(Math.round(mo)) : mo.toFixed(1).replace('.', ',');
  return `${text} Mo`;
}

export function formatMediaDate(value) {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function photoRecord(row) {
  const photo = row?.article_photo || row?.machine_content_photo;
  if (!photo) {
    return null;
  }
  const images = photo.article_photo_image || photo.machine_content_photo_image || [];
  return {
    ...photo,
    article_photo_image: images,
  };
}

function normalizeGalleryRow(row) {
  if (!row) {
    return null;
  }
  const photo = photoRecord(row);
  const kind = row.kind;
  const parsedLink = kind === 'video' ? parseVideoLink(row.external_url || (!row.key ? row.display_url : '')) : null;
  const thumbUrl = photo
    ? domainImageUrl(photo, { imageField: 'article_photo_image', size: 'small' })
    : (toMediaSrc(row.display_url_small) || toMediaSrc(row.display_url) || '');
  const mediaUrl = photo
    ? domainImageUrl(photo, { imageField: 'article_photo_image', size: 'default' })
    : (toMediaSrc(row.display_url) || row.display_url || row.key || '');

  return {
    id: row.id,
    kind,
    label: row.label || defaultGalleryLabel(kind, 0),
    originalFilename: decodeMultipartFilename(row.original_filename || ''),
    createdAt: row.created_at || null,
    sizeBytes: row.size_bytes || 0,
    externalUrl: parsedLink?.externalUrl || '',
    provider: parsedLink?.provider || '',
    providerLabel: parsedLink ? (VIDEO_PROVIDER_LABELS[parsedLink.provider] || '') : '',
    thumbUrl: thumbUrl || '',
    mediaUrl: mediaUrl || '',
    legacyPhoto: Boolean(row.legacy_photo) || (kind === 'photo' && !photo),
    photoId: photo?.id || null,
    photo: photo ? { id: photo.id, image: photo.article_photo_image?.[0] || null } : null,
    playback: kind !== 'video' ? null : (parsedLink
      ? {
        title: row.label,
        source_kind: 'link',
        provider: parsedLink.provider,
        external_url: parsedLink.externalUrl,
        embed_url: parsedLink.embedUrl,
      }
      : {
        title: row.label,
        source_kind: 'upload',
        display_url: mediaUrl,
        key: row.key || '',
      }),
  };
}

function synthesizeLegacy(item) {
  const photos = getArticlePhotos(item);
  if (photos.length) {
    return photos.map((photo, index) => {
      const filename = decodeMultipartFilename(photo.image?.name || '');
      const label = isFilenameLabel(photo.title, filename)
        ? defaultGalleryLabel('photo', index)
        : (photo.title || defaultGalleryLabel('photo', index));
      return {
        id: photo.id,
        kind: 'photo',
        label,
        originalFilename: filename,
        createdAt: null,
        sizeBytes: 0,
        externalUrl: '',
        provider: '',
        providerLabel: '',
        thumbUrl: photo.url('small') || photo.url('default') || '',
        mediaUrl: photo.url('default') || '',
        legacyPhoto: photo.isLegacy,
        photoId: photo.isLegacy ? null : photo.id,
        photo: photo.isLegacy ? null : { id: photo.id, image: photo.image },
        playback: null,
      };
    });
  }

  const media = (item?.article_media || []).find((entry) => entry.media_type === 'pdf' || entry.media_type === 'video')
    || item?.article_media?.[0];
  if (!media) {
    return [];
  }
  const isVideo = media.media_type === 'video';
  const parsed = isVideo ? parseVideoLink(media.display_url) : null;
  const isLink = Boolean(parsed) && !media.key;
  return [{
    id: media.id,
    kind: isVideo ? 'video' : 'document',
    label: defaultGalleryLabel(isVideo ? 'video' : 'document', 0),
    originalFilename: isLink ? '' : decodeMultipartFilename(media.name || ''),
    createdAt: null,
    sizeBytes: 0,
    externalUrl: parsed?.externalUrl || '',
    provider: parsed?.provider || '',
    providerLabel: parsed ? (VIDEO_PROVIDER_LABELS[parsed.provider] || '') : '',
    thumbUrl: '',
    mediaUrl: toMediaSrc(media.display_url) || media.display_url || media.key || '',
    legacyPhoto: false,
    photoId: null,
    photo: null,
    playback: !isVideo ? null : (parsed
      ? {
        title: defaultGalleryLabel('video', 0),
        source_kind: 'link',
        provider: parsed.provider,
        external_url: parsed.externalUrl,
        embed_url: parsed.embedUrl,
      }
      : {
        title: defaultGalleryLabel('video', 0),
        source_kind: 'upload',
        display_url: toMediaSrc(media.display_url) || media.display_url,
        key: media.key || '',
      }),
  }];
}

export function getContentGalleryItems(item) {
  if (!item) {
    return [];
  }
  const rows = item.article_gallery_item ?? item.machine_content_gallery_item;
  if (Array.isArray(rows)) {
    return rows.map(normalizeGalleryRow).filter(Boolean);
  }
  return synthesizeLegacy(item);
}

export function nextGalleryLabel(kind, items) {
  const count = (items || []).filter((entry) => entry.kind === kind).length;
  return defaultGalleryLabel(kind, count);
}

export const GALLERY_ZOOM_MIN = 0.5;
export const GALLERY_ZOOM_MAX = 3;
export const GALLERY_ZOOM_STEP = 0.25;

export function nextGalleryZoom(scale, direction) {
  const current = Number(scale);
  const base = Number.isFinite(current) && current > 0 ? current : 1;
  const next = Math.round((base + direction * GALLERY_ZOOM_STEP) * 100) / 100;
  return Math.min(GALLERY_ZOOM_MAX, Math.max(GALLERY_ZOOM_MIN, next));
}

export const GALLERY_AUTOPLAY_MS = 5000;
export const GALLERY_SWIPE_PX = 50;

/** Auto slideshow runs only on a photo, and only when another media can follow. */
export function galleryAutoplayMs({ open, autoplay, itemCount, kind }) {
  if (!open || !autoplay || itemCount <= 1 || kind !== 'photo') {
    return 0;
  }
  return GALLERY_AUTOPLAY_MS;
}

/** Horizontal swipe changes media. A document keeps the gesture for its own page scroll. */
export function gallerySwipeDirection(deltaX, kind) {
  if (kind === 'document' || typeof deltaX !== 'number' || Math.abs(deltaX) < GALLERY_SWIPE_PX) {
    return 0;
  }
  return deltaX < 0 ? 1 : -1;
}
